import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  TouchableOpacity,
  FlatList,
  ActivityIndicator,
  ScrollView,
} from 'react-native';
import Feather from '@expo/vector-icons/Feather';
import { useRouter } from 'expo-router';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState, AppDispatch } from '../../redux/store';
import { addItemsAndSync } from '../../redux/inventorySlice';
import { parseLufaDeliveryEmail, ParsedDeliveryItem } from '../../services/lufaImportService';
import { convertDeliveryItemsToInventory } from '../../utils/deliveryImportUtils';
import { pantryDisplayName, pantryUnitName } from '../../utils/pantryDisplayName';
import { Paprika, PaprikaTypography, PaprikaSpacing, PaprikaRadius } from '../../styles/theme';

type Step = 'paste' | 'review';

const CATEGORY_ICONS: Record<string, string> = {
  produce: 'sun',
  protein: 'box',
  dairy: 'droplet',
  bakery: 'coffee',
  frozen: 'thermometer',
  other: 'package',
};

export default function DeliveryImportScreen() {
  const router = useRouter();
  const dispatch = useDispatch<AppDispatch>();
  const { t, i18n } = useTranslation();
  const { user } = useSelector((state: RootState) => state.auth);

  const [step, setStep] = useState<Step>('paste');
  const [emailText, setEmailText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [parsedItems, setParsedItems] = useState<ParsedDeliveryItem[]>([]);
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const [deliveryDate, setDeliveryDate] = useState<string | null>(null);

  const handleParse = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const result = await parseLufaDeliveryEmail(emailText);

    setIsLoading(false);

    if (result.error) {
      console.warn('Lufa order not read:', result.error);
      setError(t('photoImport.lufaReadFailed'));
      return;
    }

    setParsedItems(result.items);
    setDeliveryDate(result.deliveryDate);
    setSelectedItems(new Set(result.items.map((_, i) => i)));
    setStep('review');
  }, [emailText]);

  const toggleItem = useCallback((index: number) => {
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  const handleAddToPantry = useCallback(async () => {
    const itemsToAdd = parsedItems.filter((_, i) => selectedItems.has(i));
    const inventoryItems = convertDeliveryItemsToInventory(itemsToAdd, deliveryDate);

    await dispatch(addItemsAndSync({
      items: inventoryItems,
      userId: user?.uid,
    }));

    if (router.canGoBack()) {
      router.back();
    } else {
      router.replace('/(app)/inventory');
    }
  }, [parsedItems, selectedItems, deliveryDate, dispatch, user, router]);

  const selectedCount = selectedItems.size;

  const handleGoBack = useCallback(() => {
    if (step === 'review') {
      setStep('paste');
      return;
    }
    if (router.canGoBack()) {
      router.back();
    }
  }, [step, router]);

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={handleGoBack}>
          <Feather name="chevron-left" size={24} color={Paprika.textPrimary} />
        </TouchableOpacity>
        <Text style={styles.title}>{t('photoImport.lufaTitle')}</Text>
        <View style={{ width: 40 }} />
      </View>

      {step === 'paste' ? (
        <ScrollView style={styles.content} keyboardShouldPersistTaps="handled">
          <Text style={styles.instruction}>{t('photoImport.lufaInstruction')}</Text>

          <TextInput
            style={styles.textArea}
            multiline
            numberOfLines={12}
            placeholder={t('photoImport.pasteLufa')}
            placeholderTextColor={Paprika.textSecondary}
            value={emailText}
            onChangeText={setEmailText}
            textAlignVertical="top"
          />

          {!!error && (
            <Text style={styles.errorText}>{error}</Text>
          )}

          {isLoading ? (
            <View testID="loading-indicator" style={styles.loadingContainer}>
              <ActivityIndicator size="large" color={Paprika.accent} />
              <Text style={styles.loadingText}>{t('photoImport.parsingOrder')}</Text>
            </View>
          ) : (
            <TouchableOpacity
              testID="parse-button"
              style={[styles.primaryButton, !emailText.trim() && styles.primaryButtonDisabled]}
              onPress={handleParse}
              disabled={!emailText.trim()}
            >
              <Feather name="search" size={18} color="#FFFFFF" />
              <Text style={styles.primaryButtonText}>{t('photoImport.parseOrder')}</Text>
            </TouchableOpacity>
          )}
        </ScrollView>
      ) : (
        <View style={styles.content}>
          <Text style={styles.itemCount}>
            {t('photoImport.itemsSelected', { count: selectedCount })}
          </Text>

          {!!deliveryDate && (
            <Text style={styles.deliveryDate}>
              {t('photoImport.deliveryDate', { date: deliveryDate })}
            </Text>
          )}

          <FlatList
            data={parsedItems}
            keyExtractor={(_, index) => index.toString()}
            style={styles.itemList}
            renderItem={({ item, index }) => {
              const isSelected = selectedItems.has(index);
              return (
                <TouchableOpacity
                  testID={`item-checkbox-${index}`}
                  style={[styles.itemRow, !isSelected && styles.itemRowDeselected]}
                  onPress={() => toggleItem(index)}
                  activeOpacity={0.7}
                >
                  <Feather
                    name={isSelected ? 'check-square' : 'square'}
                    size={20}
                    color={isSelected ? Paprika.accent : Paprika.textSecondary}
                  />
                  <Feather
                    name={(CATEGORY_ICONS[item.category] || 'package') as React.ComponentProps<typeof Feather>['name']}
                    size={16}
                    color={Paprika.textSecondary}
                    style={styles.categoryIcon}
                  />
                  <View style={styles.itemInfo}>
                    <Text style={[styles.itemName, !isSelected && styles.itemNameDeselected]}>
                      {pantryDisplayName(
                        { name: item.name, names: item.nameFr ? { en: item.name, fr: item.nameFr } : undefined },
                        i18n.language
                      )}
                    </Text>
                  </View>
                  <Text style={styles.itemQuantity}>
                    {item.quantity} {pantryUnitName(item.unit, i18n.language, item.quantity)}
                  </Text>
                  {item.isFrozen && (
                    <View style={styles.frozenBadge}>
                      <Text style={styles.frozenBadgeText}>{t('photoImport.frozen')}</Text>
                    </View>
                  )}
                </TouchableOpacity>
              );
            }}
          />

          <TouchableOpacity
            testID="add-to-pantry-button"
            style={[styles.primaryButton, selectedCount === 0 && styles.primaryButtonDisabled]}
            onPress={handleAddToPantry}
            disabled={selectedCount === 0}
          >
            <Feather name="plus" size={18} color="#FFFFFF" />
            <Text style={styles.primaryButtonText}>
              {t('photoImport.addItemsToPantry', { count: selectedCount })}
            </Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Paprika.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: PaprikaSpacing.md,
    paddingTop: PaprikaSpacing.xl,
    paddingBottom: PaprikaSpacing.md,
  },
  backButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    ...PaprikaTypography.titleLarge,
    color: Paprika.textPrimary,
  },
  content: {
    flex: 1,
    paddingHorizontal: PaprikaSpacing.md,
  },
  instruction: {
    ...PaprikaTypography.bodyLarge,
    color: Paprika.textSecondary,
    marginBottom: PaprikaSpacing.md,
  },
  textArea: {
    backgroundColor: '#F5F5F5',
    borderRadius: PaprikaRadius.md,
    padding: PaprikaSpacing.md,
    minHeight: 200,
    fontSize: 14,
    lineHeight: 20,
    color: Paprika.textPrimary,
    borderWidth: 1,
    borderColor: Paprika.border,
  },
  errorText: {
    color: Paprika.destructive,
    marginTop: PaprikaSpacing.sm,
    fontSize: 14,
  },
  loadingContainer: {
    alignItems: 'center',
    marginTop: PaprikaSpacing.xl,
  },
  loadingText: {
    ...PaprikaTypography.bodyLarge,
    color: Paprika.textSecondary,
    marginTop: PaprikaSpacing.sm,
  },
  primaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Paprika.accent,
    borderRadius: PaprikaRadius.md,
    paddingVertical: PaprikaSpacing.md,
    paddingHorizontal: PaprikaSpacing.lg,
    marginTop: PaprikaSpacing.lg,
    gap: PaprikaSpacing.sm,
  },
  primaryButtonDisabled: {
    opacity: 0.4,
  },
  primaryButtonText: {
    ...PaprikaTypography.bodyLarge,
    color: '#FFFFFF',
    fontWeight: '600',
  },
  itemCount: {
    ...PaprikaTypography.titleLarge,
    color: Paprika.textPrimary,
    marginBottom: PaprikaSpacing.xs,
  },
  deliveryDate: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textSecondary,
    marginBottom: PaprikaSpacing.md,
  },
  itemList: {
    flex: 1,
  },
  itemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: PaprikaSpacing.sm,
    paddingHorizontal: PaprikaSpacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: Paprika.border,
    gap: PaprikaSpacing.sm,
  },
  itemRowDeselected: {
    opacity: 0.4,
  },
  categoryIcon: {
    marginLeft: PaprikaSpacing.xs,
  },
  itemInfo: {
    flex: 1,
  },
  itemName: {
    ...PaprikaTypography.bodyLarge,
    color: Paprika.textPrimary,
  },
  itemNameDeselected: {
    textDecorationLine: 'line-through',
  },
  itemQuantity: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textSecondary,
  },
  frozenBadge: {
    backgroundColor: '#E3F2FD',
    borderRadius: PaprikaRadius.sm,
    paddingHorizontal: PaprikaSpacing.xs,
    paddingVertical: 2,
  },
  frozenBadgeText: {
    fontSize: 10,
    color: '#1565C0',
    fontWeight: '600',
  },
});
